/**
 * Projects page component for viewing OpenStack projects and quotas.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import {
  listProjects,
  refreshProjects,
  refreshProject,
  refreshCloudProjects,
  listClouds,
  deleteCloud,
  testCloudConnection,
  OpenStackProject,
  OpenStackCloud,
  CloudYamlUploadResponse,
} from '../api/openstack';
import { CloudUploadModal } from '../components/CloudUploadModal';
import { RefreshIcon, EditIcon, TrashIcon } from '../components/Icons';
import { QuotaGauge } from '../components/QuotaGauge';
import { updateCloud, getCloud } from '../api/openstack';
import { useWebSocket } from '../contexts/WebSocketContext';
import { logError } from '../utils/errorLogging';
import { getErrorWithContext } from '../utils/errorMessages';
import './ProjectsPage.css';

export const ProjectsPage: React.FC = () => {
  const navigate = useNavigate();
  const [clouds, setClouds] = useState<OpenStackCloud[]>([]);
  const [projects, setProjects] = useState<OpenStackProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState<Set<string>>(new Set()); // Set of cloud_ids being refreshed, or contains 'all'
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'vms'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());
  const [editingCloud, setEditingCloud] = useState<OpenStackCloud | null>(null);
  const [editCloudName, setEditCloudName] = useState<string>('');
  const [deleteConfirm, setDeleteConfirm] = useState<OpenStackCloud | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null); // cloud_id being deleted

  // Set up WebSocket connection for real-time updates
  const { isConnected, on, off } = useWebSocket();

  const loadClouds = async () => {
    try {
      const cloudList = await listClouds();
      setClouds(cloudList);
    } catch (err) {
      logError('Failed to load clouds', err);
    }
  };

  const loadProjects = async () => {
    try {
      const projectList = await listProjects();
      setProjects(projectList);
    } catch (err) {
      logError('Failed to load projects', err);
    }
  };

  // Register WebSocket event handlers for project updates
  useEffect(() => {
    const handleProjectUpdate = async (message: { event: string; data?: any }) => {
      const { event_type, cloud_id, user_id, cloud_data, project_data } = message.data || {};
      
      if (event_type === 'added') {
        // New cloud was added (could be a new upload or a share) - add to state immediately
        if (cloud_data) {
          setClouds(prevClouds => {
            // Check if already exists (avoid duplicates)
            const exists = prevClouds.some(c => c.id === cloud_id);
            if (exists) {
              return prevClouds;
            }
            return [...prevClouds, cloud_data as OpenStackCloud];
          });
          // If project_data is included, use it directly
          if (project_data && Array.isArray(project_data)) {
            setProjects(prevProjects => {
              // Merge new projects with existing ones, replacing duplicates by id
              const projectMap = new Map(prevProjects.map(p => [p.id, p]));
              project_data.forEach((p: OpenStackProject) => projectMap.set(p.id, p as OpenStackProject));
              return Array.from(projectMap.values());
            });
          } else {
            // For shared clouds, reload projects to get the associated projects
            loadProjects();
          }
        }
      } else if (event_type === 'refreshing') {
        // Refresh started - set refreshing state to show it's in progress
        if (cloud_data) {
          setRefreshing(prev => {
            const newSet = new Set(prev);
            newSet.add(String(cloud_id));
            return newSet;
          });
          setClouds(prevClouds => {
            const index = prevClouds.findIndex(c => c.id === cloud_id);
            if (index >= 0) {
              const updated = [...prevClouds];
              // Update cloud data - the refreshing state is tracked separately
              updated[index] = { 
                ...updated[index], 
                ...cloud_data as Partial<OpenStackCloud>
              };
              return updated;
            } else if (cloud_data) {
              // Add if doesn't exist yet
              return [...prevClouds, cloud_data as OpenStackCloud];
            }
            return prevClouds;
          });
        }
      } else if (event_type === 'updated') {
        // Cloud was updated (refresh completed) - always clear refreshing state first
        setRefreshing(prev => {
          const newSet = new Set(prev);
          newSet.delete(String(cloud_id));
          return newSet;
        });
        
        // Update cloud data if provided
        if (cloud_data) {
          setClouds(prevClouds => {
            const index = prevClouds.findIndex(c => c.id === cloud_id);
            if (index >= 0) {
              const updated = [...prevClouds];
              updated[index] = cloud_data as OpenStackCloud;
              return updated;
            } else {
              // Add if doesn't exist yet
              return [...prevClouds, cloud_data as OpenStackCloud];
            }
          });
          // Use project_data from WebSocket message if available, otherwise reload
          if (project_data && Array.isArray(project_data)) {
            setProjects(prevProjects => {
              // Merge new projects with existing ones, replacing duplicates by id
              const projectMap = new Map(prevProjects.map(p => [p.id, p]));
              project_data.forEach((p: OpenStackProject) => projectMap.set(p.id, p as OpenStackProject));
              return Array.from(projectMap.values());
            });
          } else {
            await loadProjects();
          }
        }
      } else if (event_type === 'deleted') {
        // Cloud was deleted - remove from state and clear refreshing state
        setRefreshing(prev => {
          const newSet = new Set(prev);
          newSet.delete(String(cloud_id));
          return newSet;
        });
        setClouds(prevClouds => prevClouds.filter(c => c.id !== cloud_id));
        // Reload projects to remove deleted project
        await loadProjects();
      }
    };

    // Register handler for project_update events
    const unsubscribe = on('project_update', handleProjectUpdate);

    // Cleanup on unmount
    return () => {
      unsubscribe();
    };
  }, [on, off]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([loadClouds(), loadProjects()]);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);



  const handleRefreshAll = async () => {
    try {
      setRefreshing(prev => new Set(prev).add('all'));
      setError(null);
      setSuccess(null);
      
      // Call the refresh all endpoint - it uses the queue system
      // WebSocket events will handle UI updates as projects are refreshed
      await refreshProjects();
      
      // Clear refreshing state immediately since refresh is enqueued
      // Note: WebSocket events will clear individual cloud refreshing states
      setRefreshing(prev => {
        const newSet = new Set(prev);
        newSet.delete('all');
        return newSet;
      });
      setSuccess('Successfully sent request to refresh');
      
    } catch (err: any) {
      logError('Failed to refresh all projects', err);
      setError(getErrorWithContext(err, 'refresh projects'));
      setRefreshing(prev => {
        const newSet = new Set(prev);
        newSet.delete('all');
        return newSet;
      });
    }
  };

  const handleRefreshCloud = async (cloudId: number) => {
    try {
      setRefreshing(prev => new Set(prev).add(String(cloudId)));
      setError(null);
      setSuccess(null);
      
      // Start refresh (don't await - let it run in background)
      refreshCloudProjects(cloudId).catch(err => {
        logError('Background refresh error', err);
      });
      
      // WebSocket events will handle UI updates, no need to poll
      // Set a timeout to clear refreshing state if WebSocket doesn't update (safety fallback)
      setTimeout(() => {
        setRefreshing(prev => {
          const newSet = new Set(prev);
          newSet.delete(String(cloudId));
          return newSet;
        });
      }, 120000); // 2 minutes fallback timeout
      
    } catch (err: any) {
      logError('Failed to refresh cloud', err);
      setError(getErrorWithContext(err, 'refresh cloud'));
      setRefreshing(prev => {
        const newSet = new Set(prev);
        newSet.delete(String(cloudId));
        return newSet;
      });
    }
  };

  const handleEditCloud = (cloud: OpenStackCloud) => {
    setEditingCloud(cloud);
    setEditCloudName(cloud.cloud_name);
  };

  const handleSaveCloudEdit = async () => {
    if (!editingCloud) return;
    
    if (!editCloudName.trim()) {
      setError('Cloud name cannot be empty');
      return;
    }

    try {
      await updateCloud(editingCloud.id, { cloud_name: editCloudName.trim() });
      setSuccess('Cloud name updated successfully');
      setEditingCloud(null);
      setEditCloudName('');
      await loadClouds();
      await loadProjects();
    } catch (err: any) {
      logError('Failed to update cloud name', err);
      setError(getErrorWithContext(err, 'update cloud name'));
    }
  };

  const handleCancelEdit = () => {
    setEditingCloud(null);
    setEditCloudName('');
  };

  const handleDeleteCloud = async (cloud: OpenStackCloud) => {
    setDeleteConfirm(cloud);
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirm) return;
    
    setDeleting(deleteConfirm.id);
    try {
      await deleteCloud(deleteConfirm.id);
      setSuccess('Cloud deleted successfully');
      setDeleteConfirm(null);
      await loadClouds();
      await loadProjects();
    } catch (err) {
      logError('Failed to delete cloud', err);
      setError(getErrorWithContext(err, 'delete cloud'));
      setDeleteConfirm(null);
    } finally {
      setDeleting(null);
    }
  };

  const formatBytes = (bytes: number, unit: 'MB' | 'GB' = 'GB'): string => {
    if (unit === 'MB') {
      if (bytes < 1024) {
        return `${bytes} MB`;
      }
      return `${(bytes / 1024).toFixed(2)} GB`;
    } else {
      if (bytes < 1024) {
        return `${bytes} GB`;
      }
      return `${(bytes / 1024).toFixed(2)} TB`;
    }
  };

  // Removed quota percentage/color functions - not needed for table view

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatRelativeTime = (dateString: string): string => {
    // Parse the UTC date string and convert to local time
    let dateStr = dateString.trim();
    
    // Check if the string has timezone information (Z, +HH:MM, or -HH:MM)
    const hasTimezone = dateStr.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(dateStr);
    
    if (!hasTimezone) {
      // No timezone info - assume UTC and append 'Z'
      dateStr = dateStr.replace(/\s+$/, '');
      if (dateStr.includes('T')) {
        dateStr = dateStr + 'Z';
      } else {
        dateStr = dateStr + 'T00:00:00Z';
      }
    }
    
    const date = new Date(dateStr);
    
    // Verify the date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffSeconds < 60) {
      return `${diffSeconds} second${diffSeconds !== 1 ? 's' : ''} ago`;
    } else if (diffMinutes < 60) {
      return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    } else {
      // For longer periods, show the date
      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  };

  const getTimeAgoColor = (dateString: string): string => {
    if (!dateString) return 'var(--text-color)';
    
    let dateStr = dateString.trim();
    const hasTimezone = dateStr.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(dateStr);
    
    if (!hasTimezone) {
      dateStr = dateStr.replace(/\s+$/, '');
      if (dateStr.includes('T')) {
        dateStr = dateStr + 'Z';
      } else {
        dateStr = dateStr + 'T00:00:00Z';
      }
    }
    
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      return 'var(--text-color)';
    }
    
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffHours < 1) {
      return 'var(--success-color)'; // Green - less than 1 hour
    } else if (diffDays < 1) {
      return 'var(--warning-color)'; // Yellow - less than 24 hours
    } else {
      return 'var(--error-color)'; // Red - 24 hours or more
    }
  };

  const getCloudUpdated = (cloud: OpenStackCloud): { text: string; color: string } => {
    // Check if this cloud is currently being refreshed
    const isRefreshing = refreshing.has(String(cloud.id)) || refreshing.has('all');
    
    if (isRefreshing) {
      return {
        text: 'Refreshing...',
        color: 'var(--text-secondary)'
      };
    }
    
    if (cloud.status === 'refreshed' && cloud.last_refreshed_at) {
      return {
        text: formatRelativeTime(cloud.last_refreshed_at),
        color: getTimeAgoColor(cloud.last_refreshed_at)
      };
    }
    
    if (cloud.status === 'error') {
      return {
        text: 'ERROR',
        color: 'var(--error-color)'
      };
    }
    
    return {
      text: 'Uploaded',
      color: 'var(--text-secondary)'
    };
  };

  // Find project data for a cloud (if any projects exist for this cloud)
  const getProjectForCloud = (cloud: OpenStackCloud): OpenStackProject | undefined => {
    // Try to match by cloud_id - projects have cloud_id
    return projects.find(p => p.cloud_id === cloud.id);
  };

  // Get display name for a project (use cloud_name as editable user-friendly name)
  const getProjectDisplayName = (cloud: OpenStackCloud): string => {
    return cloud.cloud_name;
  };

  // Toggle card expansion
  const toggleCardExpansion = (cloudId: number) => {
    setExpandedCards((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(cloudId)) {
        newSet.delete(cloudId);
      } else {
        newSet.add(cloudId);
      }
      return newSet;
    });
  };

  // Calculate percentage for gauge
  const calculatePercentage = (allocated: number, quota?: number | null): number => {
    if (!quota || quota <= 0) return 0;
    return Math.min(100, Math.round((allocated / quota) * 100));
  };

  // Get gauge color based on percentage
  const getGaugeColor = (percentage: number): string => {
    if (percentage >= 90) return 'var(--error-color)'; // Red for 90%+
    if (percentage >= 70) return 'var(--warning-color)'; // Yellow for 70-89%
    return 'var(--success-color)'; // Green for <70%
  };

  // Filter and sort clouds
  const filteredAndSortedClouds = React.useMemo(() => {
    let filtered = (clouds || []).filter((cloud) => {
      const project = getProjectForCloud(cloud);
      const displayName = getProjectDisplayName(cloud).toLowerCase();
      const searchLower = searchQuery.toLowerCase();
      return displayName.includes(searchLower) || 
             cloud.cloud_name.toLowerCase().includes(searchLower) ||
             (project?.project_name && project.project_name.toLowerCase().includes(searchLower));
    });

    // Sort
    filtered.sort((a, b) => {
      const projectA = getProjectForCloud(a);
      const projectB = getProjectForCloud(b);
      
      if (sortBy === 'name') {
        const nameA = getProjectDisplayName(a).toLowerCase();
        const nameB = getProjectDisplayName(b).toLowerCase();
        const comparison = nameA.localeCompare(nameB);
        return sortOrder === 'asc' ? comparison : -comparison;
      } else if (sortBy === 'vms') {
        const vmsA = projectA?.quotas.instances_allocated || 0;
        const vmsB = projectB?.quotas.instances_allocated || 0;
        // Default to descending (highest first) when sorting by VMs
        return sortOrder === 'desc' ? vmsB - vmsA : vmsA - vmsB;
      }
      return 0;
    });

    return filtered;
  }, [clouds, projects, searchQuery, sortBy, sortOrder]);

  return (
    <Layout>
      <div className="projects-page">
        <div className="projects-container">
          <h1>Projects</h1>
          <div className="projects-header">
            <p className="page-description">
              View and manage your OpenStack clouds, projects, and quota usage.
            </p>
            <div className="projects-actions">
              <button
                className="btn-secondary"
                onClick={() => setShowUploadModal(true)}
              >
                Add Cloud
              </button>
              <button
                className="btn-primary"
                onClick={handleRefreshAll}
                disabled={refreshing.has('all')}
              >
                {refreshing.has('all') ? 'Refreshing...' : 'Refresh All'}
              </button>
            </div>
          </div>

          {error && (
            <div className="error-message" role="alert">
              <strong>Error:</strong> {error}
            </div>
          )}

          {success && (
            <div className="success-message" role="alert">
              <strong>Success:</strong> {success}
            </div>
          )}

          {loading ? (
            <div className="loading-message">Loading...</div>
          ) : (
            <div className="projects-section">
              {/* Search and Sort Controls */}
              <div className="projects-controls">
                <div className="search-container">
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Search projects..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="sort-container">
                  <label htmlFor="sort-by">Sort by:</label>
                  <select
                    id="sort-by"
                    className="sort-select"
                    value={sortBy}
                    onChange={(e) => {
                      const newSortBy = e.target.value as 'name' | 'vms';
                      setSortBy(newSortBy);
                      // When sorting by VMs, default to descending (highest first)
                      if (newSortBy === 'vms') {
                        setSortOrder('desc');
                      } else {
                        setSortOrder('asc');
                      }
                    }}
                  >
                    <option value="name">Name</option>
                    <option value="vms">VMs (Allocated)</option>
                  </select>
                  <button
                    className="btn-icon sort-order-btn"
                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                    title={`Sort ${sortOrder === 'asc' ? 'descending' : 'ascending'}`}
                  >
                    {sortOrder === 'asc' ? '↑' : '↓'}
                  </button>
                </div>
              </div>

              {/* Projects Cards Grid */}
              {filteredAndSortedClouds.length > 0 ? (
                <div className="projects-grid">
                  {filteredAndSortedClouds.map((cloud) => {
                    const project = getProjectForCloud(cloud);
                    const displayName = getProjectDisplayName(cloud);
                    const updatedInfo = getCloudUpdated(cloud);
                    
                    return (
                      <div 
                        key={cloud.id} 
                        className="project-card"
                        onClick={(e) => {
                          // Don't navigate if clicking on action buttons, edit form, or expand button
                          const target = e.target as HTMLElement;
                          if (
                            target.closest('.project-card-actions') ||
                            target.closest('.project-card-edit-form') ||
                            target.closest('.quota-expand-btn') ||
                            editingCloud?.id === cloud.id
                          ) {
                            return;
                          }
                          // Navigate to project detail page if project exists
                          if (project) {
                            navigate(`/projects/${project.project_id}`);
                          }
                        }}
                        style={{ cursor: project ? 'pointer' : 'default' }}
                      >
                        <div className="project-card-header">
                          <div className="project-card-header-content">
                            {editingCloud?.id === cloud.id ? (
                              <div className="project-card-edit-form">
                                <input
                                  type="text"
                                  value={editCloudName}
                                  onChange={(e) => setEditCloudName(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSaveCloudEdit();
                                    if (e.key === 'Escape') handleCancelEdit();
                                  }}
                                  className="project-card-edit-input"
                                  autoFocus
                                />
                                <div className="project-card-edit-actions">
                                  <button
                                    className="btn-icon"
                                    onClick={handleSaveCloudEdit}
                                    title="Save"
                                    aria-label="Save"
                                  >
                                    ✓
                                  </button>
                                  <button
                                    className="btn-icon"
                                    onClick={handleCancelEdit}
                                    title="Cancel"
                                    aria-label="Cancel"
                                  >
                                    ✕
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <h3 
                                  className="project-card-title" 
                                  title={displayName.length > 0 ? displayName : undefined}
                                >
                                  {displayName}
                                </h3>
                                {project?.project_name && (
                                  <div 
                                    className="project-card-subtitle" 
                                    title={project.project_name.length > 0 ? project.project_name : undefined}
                                  >
                                    {project.project_name}
                                  </div>
                                )}
                                <div className="project-card-status" style={{ color: updatedInfo.color }}>
                                  {updatedInfo.text}
                                </div>
                              </>
                            )}
                          </div>
                          {editingCloud?.id !== cloud.id && (
                            <div className="project-card-actions">
                              <button
                                className="btn-icon"
                                onClick={() => handleRefreshCloud(cloud.id)}
                                disabled={refreshing.has(String(cloud.id)) || refreshing.has('all')}
                                title="Refresh"
                                aria-label="Refresh"
                              >
                                <RefreshIcon />
                              </button>
                              <button
                                className="btn-icon"
                                onClick={() => handleEditCloud(cloud)}
                                title="Edit"
                                aria-label="Edit"
                              >
                                <EditIcon />
                              </button>
                              <button
                                className="btn-icon"
                                onClick={() => handleDeleteCloud(cloud)}
                                title="Delete"
                                aria-label="Delete"
                              >
                                <TrashIcon />
                              </button>
                            </div>
                          )}
                        </div>
                        <div className="project-card-content">
                          {project && (
                            <>
                              <div className="project-card-quotas">
                                {/* Main quotas shown by default - VMs, Cores, RAM, Disk */}
                                <QuotaGauge
                                  label="VMs"
                                  allocated={project.quotas.instances_allocated}
                                  quota={project.quotas.instances_quota}
                                />
                                <QuotaGauge
                                  label="Cores"
                                  allocated={project.quotas.cores_allocated}
                                  quota={project.quotas.cores_quota}
                                />
                                <QuotaGauge
                                  label="RAM (GB)"
                                  allocated={Math.round(project.quotas.memory_allocated / 1024)}
                                  quota={project.quotas.memory_quota ? Math.round(project.quotas.memory_quota / 1024) : undefined}
                                />
                                <QuotaGauge
                                  label="Disk (GB)"
                                  allocated={Math.round(project.quotas.volume_storage_allocated)}
                                  quota={project.quotas.volume_storage_quota ? Math.round(project.quotas.volume_storage_quota) : undefined}
                                />
                                
                                {/* Additional quotas shown when expanded */}
                                {expandedCards.has(cloud.id) && (
                                  <>
                                    <QuotaGauge
                                      label="Volumes"
                                      allocated={project.quotas.volumes_allocated}
                                      quota={project.quotas.volumes_quota}
                                    />
                                    <QuotaGauge
                                      label="Floating IPs"
                                      allocated={project.quotas.floating_ips_allocated}
                                      quota={project.quotas.floating_ips_quota}
                                    />
                                    <QuotaGauge
                                      label="Security Groups"
                                      allocated={project.quotas.security_groups_allocated}
                                      quota={project.quotas.security_groups_quota}
                                    />
                                    <QuotaGauge
                                      label="Networks"
                                      allocated={project.quotas.networks_allocated}
                                      quota={project.quotas.networks_quota}
                                    />
                                  </>
                                )}
                              </div>
                              
                              {/* Expand/Collapse button */}
                              <button
                                className="quota-expand-btn"
                                onClick={() => toggleCardExpansion(cloud.id)}
                                aria-label={expandedCards.has(cloud.id) ? 'Hide additional quotas' : 'Show additional quotas'}
                              >
                                <span className="quota-expand-text">
                                  {expandedCards.has(cloud.id) ? 'Show Less' : 'Show More'}
                                </span>
                                <span className={`quota-expand-icon ${expandedCards.has(cloud.id) ? 'expanded' : ''}`}>
                                  ▼
                                </span>
                              </button>
                            </>
                          )}
                          {!project && (
                            <div className="project-card-no-data">
                              No quota data available. Click refresh to fetch data.
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="projects-empty">
                  <p>{searchQuery ? 'No projects match your search.' : 'No clouds found.'}</p>
                  <p>Upload a cloud.yaml file to get started.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <CloudUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onSuccess={(uploadResponse: CloudYamlUploadResponse) => {
          setShowUploadModal(false);
          
          // Use the message from the backend response if available
          if (uploadResponse.message) {
            setSuccess(uploadResponse.message);
            // Clear success message after 8 seconds (longer to read rejection details)
            setTimeout(() => setSuccess(null), 8000);
          } else if (uploadResponse.clouds && uploadResponse.clouds.length > 0) {
            // Fallback to basic message if backend didn't provide one
            const message = `Successfully processed ${uploadResponse.clouds.length} cloud(s).`;
            setSuccess(message);
            setTimeout(() => setSuccess(null), 5000);
          }
          
          loadClouds();
          loadProjects();
        }}
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Confirm Delete</h3>
            <p>
              Are you sure you want to delete the cloud configuration <strong>&quot;{deleteConfirm.cloud_name}&quot;</strong>? This action cannot be undone.
            </p>
            <div className="modal-actions">
              <button
                className="btn-cancel"
                onClick={() => setDeleteConfirm(null)}
              >
                Cancel
              </button>
              <button
                className="btn-confirm-delete"
                onClick={handleConfirmDelete}
                disabled={deleting !== null}
              >
                {deleting !== null ? 'Deleting...' : 'Delete Cloud'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

